
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate } from "@/lib/format";
import { printStatementOfAccount } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function localDate(first = false) {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${first ? "01" : String(d.getDate()).padStart(2,"0")}`;
}
export default function StatementOfAccount() {
  const { business, settings, currency, dateFormat } = useSettings();
  const [scope, setScope] = useState("business");
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [start, setStart] = useState(() => localDate(true));
  const [end, setEnd] = useState(() => localDate());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);
  useEffect(() => {
    api.get("/customers").then(({ data }) => {
      const rows = Array.isArray(data) ? data : data.customers || data.items;
      if (!Array.isArray(rows)) throw new Error("Unexpected customer list");
      setCustomers(rows);
    }).catch(() => toast.error("Unable to load customer list"));
  }, []);
  const invalidate = () => {
    request.current += 1;
    setReport(null);
    setLoading(false);
  };
  const generate = async (automatic = false) => {
    if (!automatic && (!start || !end || start > end)) return toast.error("Select a valid date range");
    if (scope === "customer" && !customer) return toast.error("Select a customer");
    const id = ++request.current;
    setLoading(true);
    setReport(null);
    try {
      const { data } = await api.get("/reports/statement-of-account", {
        params: { start_date: automatic ? undefined : start, end_date: automatic ? localDate() : end,
          customer_id: scope === "customer" ? customer : "" }
      });
      if (id === request.current) {
        setReport(data);
        if (automatic) {
          setStart(data.start_date);
          setEnd(data.end_date);
        }
      }
    } catch (e) {
      if (id === request.current) toast.error(apiError(e.response?.data?.detail));
    } finally {
      if (id === request.current) setLoading(false);
    }
  };
  useEffect(() => {
    if (scope === "customer" && !customer) return;
    generate(true);
    // Reload the full account whenever the selected customer changes.
  }, [scope, customer]);

  const money = value => fmtMoney(value || 0, currency);
  return <div className="space-y-6">
    <h1 className="font-heading text-3xl font-bold text-navy">Statement of Account</h1>
    <div className="bg-white border rounded-xl p-5 flex flex-wrap items-end gap-4">
      <label className="text-sm text-navy">Statement
        <select className="block border rounded-md p-2 mt-2" value={scope}
          onChange={e => { invalidate(); setScope(e.target.value); }}>
          <option value="business">Business</option><option value="customer">Customer</option>
        </select>
      </label>
      {scope === "customer" && <label className="text-sm text-navy">Customer
        <select className="block border rounded-md p-2 mt-2 max-w-xs" value={customer}
          onChange={e => { invalidate(); setCustomer(e.target.value); }}>
          <option value="">Select customer</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone ? `— ${c.phone}` : ""}</option>)}
        </select>
      </label>}
      <label className="text-sm text-navy">From
        <Input className="mt-2" type="date" value={start}
          onChange={e => { invalidate(); setStart(e.target.value); }} />
      </label>
      <label className="text-sm text-navy">To
        <Input className="mt-2" type="date" value={end}
          onChange={e => { invalidate(); setEnd(e.target.value); }} />
      </label>
      <Button className="bg-navy" onClick={() => generate(false)} disabled={loading}>
        {loading ? "Loading…" : "Generate"}
      </Button>
      {report && <Button variant="outline"
        onClick={() => printStatementOfAccount(report, business, settings)}>Print PDF</Button>}
    </div>
    {report && <>
      <div className="text-navy font-semibold">
        {report.scope === "customer" ? report.customer_name : "Business"} · {fmtDate(report.start_date, dateFormat)} to {fmtDate(report.end_date, dateFormat)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[["Opening Balance", report.opening_balance], ["Total Debit", report.total_debit],
          ["Total Credit", report.total_credit], [`Closing ${report.balance_label}`, report.closing_balance]]
          .map(([label, value]) => <div key={label} className="bg-white border rounded-xl p-5">
            <div className="text-sm text-slate-500">{label}</div>
            <div className="font-mono text-xl font-bold text-navy mt-2">{money(value)}</div>
          </div>)}
      </div>
      <p className="text-sm text-slate-500">{report.note}</p>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50"><tr>
            {["Sl.No", "Date", "Particulars", "Reference", "Debit", "Credit", report.balance_label]
              .map((label, i) => <th key={label} className={`p-3 ${i >= 4 ? "text-right" : "text-left"}`}>{label}</th>)}
          </tr></thead>
          <tbody>
            <tr className="border-t bg-slate-50"><td colSpan={6} className="p-3 font-semibold">Opening balance</td>
              <td className="p-3 text-right font-mono">{money(report.opening_balance)}</td></tr>
            {report.rows.map((row, i) => <tr key={i} className="border-t">
              <td className="p-3">{i+1}</td><td className="p-3 whitespace-nowrap">{fmtDate(row.date, dateFormat)}</td>
              <td className="p-3">{row.particulars}</td><td className="p-3">{row.reference}</td>
              <td className="p-3 text-right font-mono">{row.debit ? money(row.debit) : "—"}</td>
              <td className="p-3 text-right font-mono">{row.credit ? money(row.credit) : "—"}</td>
              <td className="p-3 text-right font-mono">{money(row.balance)}</td>
            </tr>)}
            {!report.rows.length && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No transactions in this period.</td></tr>}
          </tbody>
          <tfoot className="border-t bg-slate-50 font-bold"><tr>
            <td colSpan={4} className="p-3">Closing totals</td>
            <td className="p-3 text-right">{money(report.total_debit)}</td>
            <td className="p-3 text-right">{money(report.total_credit)}</td>
            <td className="p-3 text-right">{money(report.closing_balance)}</td>
          </tr></tfoot>
        </table>
      </div>
    </>}
  </div>;
}
